import React, { useContext, useEffect, useState } from 'react';
import { EMPTY_CELL } from './emptyCell';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Ticket } from '../../interfaces/ticket.interface';
import {
  getCategory,
  getGroup,
  getPriority,
  getStatus,
  getTicketType,
  getTeam,
  getResolutionCategories
} from '../../../tikit/ticketHelper';
import { appState, AppStateValue } from '../../../AppState';
import { ErrorIcon, Skeleton } from '@fluentui/react-northstar';
import FlashlightSVG from './../../../../svg/flashlight.svg';
import EmptySVG from './../../../../svg/empty.svg';
import { EmptyData } from '../EmptyData';
import ClassDetailList from './ClassDetailList';
import { NON_SORTABLE_COLUMNS } from '../../utils/constants';
import { showParentChild } from '../TicketDetailInfo/TicketDetailInfo';
import { ListContext } from '../../../tikit/List/ListContextProvider';
import { CustomViewContext, EnumSortOrderType } from '../../../tikit/toolbar/CustomViewContextProvider';
import ListFunctions, { DEFAULT_SORT_ORDER_FOR_QUERYING } from '../../../tikit/List/ListFunctions';
import { getConfiguredColumnsKeyOrderFromStorage, getSortOrderFromStorage, setSortOrderInStorage, ticketListPreferenceKey } from '../../../tikit/toolbar/CustomViewPopup/heplers/customPopupHelper';
import { ListPreference } from './listPreferences';
import { TicketProps } from '../../interfaces/ticketproperties.interface';
import { withArchived } from '../../utils/archivedPickers';
import { findWithArchived } from '../../utils/archivedLookups';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormQuestion } from '../../../tikit/List/formColumns';
import { viewFormQuestions } from '../../../tikit/List/templateQuestions';
export interface TicketProp extends Ticket {
  key: string;
  AssigneeName: string;
  RequesterName: string;
  TeamName: string;
  GroupName: string;
  Status: string;
  Category: string;
  Priority: string;
  TicketType: string;
  TicketCollaboratorCount: PlatormUserEntity[];
  AffectedUsersCount: any[];
  TicketLifecycleName: string;
  TicketLifecyclePhaseName: string;
  TemplateName?: string;
  SourceName?: string;
  ResolutionCategory: string;
  CustomPropertiesAnswerJson?: string;
  /** Maintained on the ticket by TicketTimeEntryTrigger, not summed on the client. */
  TotalTimeSpentMinutes?: number;
}

interface ListProps {
  appStateValue: AppStateValue;
  filterDropdownCount: number;
  onTicketsSelected: (tickets: Ticket[]) => void;
  isFilterApplied: boolean;
  listFeatureStates: ListFeatureStates;
}

type ScopeState = {
  tickets: TicketProp[];
  columns: IColumn[];
};

const defaultState = {
  items: [],
  isModalSelection: false,
  isCompactMode: false,
  useTicketType: false,
  selectedOrderedColumns: [] as columnsKeyOrderType,
  useTeam: false,
  useTicketLifecycle: false,
  useSLA: false,
  userResolutionNote: false,
  useGroupChat: false,
  // Off until the licence flags resolve, so the Total Agent Time column is never drawn
  // on the assumption that a tenant has the feature.
  useTimeTracking: false,
  useTicketTags: false,
  useTicketTemplates: false
};

const defaultFilter = {
  listFilter: 0,
  filterDropdownCount: 0,
};

export const SkeletonCell = (props: any) => {
  return (
    <Skeleton animation='wave'>
      <Skeleton.Line
        height='20px'
        width={props.width || '200px'}
      ></Skeleton.Line>
    </Skeleton>
  );
};

export const TicketListBoard = (props: ListProps) => {
  const { appStateValue, onTicketsSelected, listFeatureStates } = props;

  const { listStates, setListStates } = useContext(ListContext);
  const [formColumns, setFormColumns] = useState<FormQuestion[]>([]);
  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const currentstate = appState();
  const { getpaginatedTicketsOnEdit } = ListFunctions();

  const scope: ScopeState = { tickets: [], columns: [] };
  const [ticketState, setTicketState] = useState(Object.assign(defaultState, listFeatureStates));
  const [isLoaded, setIsLoaded] = useState(false);
  const [filterState, setFilterState] = useState(defaultFilter);
  const { t } = useTranslation();

  const setupTicketState = async (isTemp: boolean = false) => {
    let mapTickets = (listStates.tickets as Ticket[]).map((item, i) => toTicketRow(item, i, currentstate, t));

    scope.tickets = mapTickets;
    let newTickets = scope.tickets;
    if (isTemp) {
      newTickets = newTickets.filter((x) => x.Id >= 1 || x.Title !== 'temp');
      newTickets.unshift({ Title: 'temp_ticket' } as TicketProp);
    }

    setTicketState(prev => ({
      ...prev,
      items: newTickets,
      isModalSelection: false,
      isCompactMode: false
    }));
  };

  const clickColumnCallback = (column: IColumn) => {
    if(NON_SORTABLE_COLUMNS.some(x => Number(x) == Number(column.key)))
      return;
    const prevSortOrder = customViewStates.configureColumnsApplied.sortOrder;
    const sortOrder = {
      orderBy: Number(column.key) as ISortOrderBy,
      orderType: column.isSortedDescending ? EnumSortOrderType.asc : EnumSortOrderType.desc
    } as ISortOrder;

    
    setCustomViewStates(prev => ({
      ...prev,
      configureColumnsApplied: {
        prevSortOrder,
        sortOrder: sortOrder,
        keepRows: true
      }
    }));
    setSortOrderInStorage(sortOrder);
  }

  // The column the list was sorted by was removed: back to the sort the view was saved with, as Reset View does.
  const onSortedColumnRemoved = () => {
    const { selectedCustomView } = customViewStates;
    const appliedSortOrder = getSortOrderFromStorage() || customViewStates.configureColumnsApplied.sortOrder;
    const viewSortOrder = selectedCustomView?.OrderBy
      ? { orderBy: selectedCustomView.OrderBy, orderType: selectedCustomView.OrderType }
      : null;
    setSortOrderInStorage(null);
    setCustomViewStates(prev => ({
      ...prev,
      configureColumnsApplied: { prevSortOrder: appliedSortOrder, sortOrder: viewSortOrder, keepRows: true },
    }));
  };

  useEffect(() => {
    if (
      isLoaded &&
      (appStateValue.listFilter !== filterState.listFilter ||
        appStateValue.filterDropdownCount !== filterState.filterDropdownCount)
    ) {
      setFilterState({
        listFilter: appStateValue.listFilter,
        filterDropdownCount: appStateValue.filterDropdownCount,
      });
      setupTicketState();
    }

    if ((listStates.tickets as Ticket[]).length > 0) {
      if (!isLoaded) {
        setIsLoaded(true);
      }
      if (listStates.tickets.some((t, i) => t.Title === 'temp')) {
        setupTicketState(true);
      } else {
        setupTicketState();
      }
    }

    if (customViewStates.selectedCustomView) {
      const { ColumnsSelectedValues } = customViewStates.selectedCustomView;
      if (ColumnsSelectedValues?.columnsKeyOrder) {
        setTicketState(prev => ({
          ...prev,
          selectedOrderedColumns: ColumnsSelectedValues.columnsKeyOrder,
        }));
      }
    }

    if (!customViewStates.selectedCustomView) {
      const orderedColumns = getConfiguredColumnsKeyOrderFromStorage() || customViewStates.configurableColumnsKeyOrder;
      setTicketState(prev => ({
        ...prev,
        selectedOrderedColumns: orderedColumns,
      }));
    }

  }, [props]);

  // The view's form question columns: only with one usable template in its filter (viewFormQuestions).
  useEffect(() => {
    let live = true;
    const view = ticketState.useTicketTemplates ? customViewStates.selectedCustomView : null;
    viewFormQuestions(view).then(questions => { if (live) setFormColumns(questions); });
    return () => { live = false; };
  }, [customViewStates.selectedCustomView, ticketState.useTicketTemplates]);

  const sortOrderApplied =
    getSortOrderFromStorage() ||
    customViewStates.configureColumnsApplied.sortOrder ||
    DEFAULT_SORT_ORDER_FOR_QUERYING;
  return (
    <>
      {listStates.tickets.length > 0 ? (
        <ClassDetailList
          tickets={ticketState.items}
          onTicketsSelected={onTicketsSelected}
          currentState={currentstate}
          useTicketType={ticketState.useTicketType}
          useTeam={ticketState.useTeam}
          useTicketLifecycle={ticketState.useTicketLifecycle}
          useSLA={ticketState.useSLA}
          useResolutionNote={ticketState.userResolutionNote}
          setListStates={setListStates}
          selectedOrderedColumns={[...ticketState.selectedOrderedColumns]}
          sortOrderApplied={sortOrderApplied}
          clickColumnCallback={clickColumnCallback}
          useGroupChat={ticketState.useGroupChat}
          useCustomProperties={ticketState.useCustomProperties}
          useTimeTracking={ticketState.useTimeTracking}
          useTicketTags={ticketState.useTicketTags}
          useTicketTemplates={ticketState.useTicketTemplates}
          formColumns={formColumns}
          customViewStates={customViewStates}
          columnWidthsKey={ticketListPreferenceKey(ListPreference.TicketListColumnWidths)}
          columnOrderKey={ticketListPreferenceKey(ListPreference.TicketListColumnOrder)}
          hiddenColumnsKey={ticketListPreferenceKey(ListPreference.TicketListHiddenColumns)}
          onSortedColumnRemoved={onSortedColumnRemoved}
          resolutionNoteConfig={ticketState.resolutionNoteConfig}
          onTicketEdited={() => getpaginatedTicketsOnEdit(listStates.currentPage, listStates.countPerPage, { listStates, setListStates }, customViewStates)}
        />
      ) : (
        <>
          {currentstate.filtersApplyCount > 0 ? (
            <EmptyData
              SVGIcon={<FlashlightSVG width={200} height={200} className={'pb-2'} /> }
              headerText={t('ticket-details.edit-ticket-view.empty.header-text')}
              subheaderText={t('common.try-a-different-search')} />
          ) : 
          listStates.errorDuringFetch ? (
            <EmptyData
              headerText="An error occurred loading the tickets."
              subheaderText={'Please check your filters to ensure they are correct'}
              SVGIcon={<ErrorIcon size='largest' />} />
          ) : (
            <EmptyData
              headerText="There are no Tickets, let's create one."
              subheaderText={'Get started by clicking the Add Ticket button in the toolbar above.'}
              SVGIcon={<EmptySVG height={200} width={200} />} />
          )}
        </>
      )}
    </>
  );
};

/**
 * The Template column's text: '-' for no template (Shaun, 2026-10-14 - tried 'No Template', went back). A template the agent
 * is scoped out of reads 'No access': its name is shown only if it is among the templates the server lists for this
 * agent (TicketTemplate is scoped there), whatever the ticket's expanded Template carries.
 */
const templateName = (item: Ticket, currentstate: AppStateValue, t: TFunction): string => {
  if (!item.TemplateId) return EMPTY_CELL;
  const usable = currentstate?.ticketTemplates;
  const allowed = !usable || usable.some(template => template.Id === item.TemplateId);
  return allowed && item.Template?.Name ? item.Template.Name : t('ticket.child-filter.no-access');
};

export const toTicketRow = (item: Ticket, i: number, currentstate: AppStateValue, t: TFunction): TicketProp => {
  const getTeamName = () => {
    return item['Team']
      ? item['Team']['FriendlyName']
      : getTeam(item.TeamId, currentstate)?.FriendlyName;
  }
  const getSupportGroupName = () => {
    return item['SupportGroup']
      ? item['SupportGroup']['Name']
      : getGroup(item.SupportGroupId, currentstate)?.Name;
  }
  const statuses = withArchived(currentstate.ticketStatus, currentstate.archivedTicketStatus);
  const categories = withArchived(currentstate.categories, currentstate.archivedCategories);
  const priorities = withArchived(currentstate.priority, currentstate.archivedPriority);
  const ticketTypes = withArchived(currentstate.ticketTypes, currentstate.archivedTicketTypes);

  return {
    ...item,
    key: `column${i + 1}`,
    AssigneeName: item.AssigneeId == null ? t('ticket.ticket-board.ticket-card.unassigned') : item.Assignee?.FullName,
    RequesterName: item.RequesterId == null ? t('ticket.ticket-board.ticket-card.unassigned') : item.Requester?.FullName,
    TeamName: item['TeamId'] == null ? EMPTY_CELL : getTeamName(),
    GroupName: item['SupportGroupId'] == null ? EMPTY_CELL : getSupportGroupName(),
    Status:
      item.StatusId == null
        ? EMPTY_CELL
        : showParentChild(
            getStatus(item.StatusId, statuses),
            statuses
          ).join(' > '),
    Category:
      item.CategoryId == null
        ? EMPTY_CELL
        : showParentChild(
            getCategory(item.CategoryId, categories),
            categories
          ).join(' > '),
    ResolutionCategory:
      item?.Resolution?.ResolutionCategoryId == null
        ? ""
        : showParentChild(
          getResolutionCategories(item?.Resolution?.ResolutionCategoryId, currentstate.resolutionCategory),
            currentstate.resolutionCategory
          ).join(' > '),
    Priority:
      item.PriorityId == null
        ? EMPTY_CELL
        : showParentChild(
            getPriority(item.PriorityId, priorities),
            priorities
          ).join(' > '),
    TicketType:
      item.TicketTypeId == null
        ? EMPTY_CELL
        : showParentChild(
            getTicketType(item.TicketTypeId, ticketTypes),
            ticketTypes
          ).join(' > '),
    TicketCollaboratorCount: item.TicketCollaborators,
    AffectedUsersCount: item.AffectedUsers,
    TicketLifecycleName: item.TicketLifecycle?.Lifecycle?.Title ?? EMPTY_CELL,
    TicketLifecyclePhaseName: item.TicketLifecycle?.Phases?.[0]?.Name ?? '',
    TemplateName: templateName(item, currentstate, t),
    SourceName: findWithArchived(currentstate.source, item.SourceId)?.Value ?? EMPTY_CELL
  };
};

export const getSupportGroupName = (item: Ticket, currentstate: AppStateValue) => {
  return item['SupportGroup']
    ? item['SupportGroup']['Name']
    : getGroup(item.SupportGroupId, currentstate)?.Name;
}

export const getTeamName = (item: Ticket, currentstate: AppStateValue) => {
  return item['Team']
    ? item['Team']['FriendlyName']
    : getTeam(item.TeamId, currentstate)?.FriendlyName;
}

export const hierarchialStatusName = ({
  statusId,
  ticketStatuses,
}: {
  statusId?: number;
  ticketStatuses: TicketProps[];
}) => {
  return statusId == null
    ? 'No Status'
    : showParentChild(getStatus(statusId, ticketStatuses), ticketStatuses).join(
        ' > '
      );
};

export const hierarchialCategoryName = ({
  categoryId,
  categories,
}: {
  categoryId?: number;
  categories: TicketProps[];
}) => {
  return categoryId == null
    ? 'No Category'
    : showParentChild(getCategory(categoryId, categories), categories).join(
        ' > '
      );
};

export const hierarchialPriorityName = ({
  priorityId,
  priorities,
}: {
  priorityId?: number;
  priorities: TicketProps[];
}) => {
  return priorityId == null
    ? 'No Priority'
    : showParentChild(getPriority(priorityId, priorities), priorities).join(
        ' > '
      );
};

export const hierarchialTicketType = ({
  ticketTypeId,
  ticketTypes,
}: {
  ticketTypeId?: number;
  ticketTypes: TicketProps[];
}) => {
  return ticketTypeId == null
    ? 'No Type'
    : showParentChild(
        getTicketType(ticketTypeId, ticketTypes),
        ticketTypes
      ).join(' > ');
};
